/**
 * A remote room lets one device drive another over the internet.
 *
 * Everything here is pure: the same validation runs in the browser and in the
 * worker, so the two cannot disagree about what a legal state is.
 */

export type RemoteState = {
  page: number;
  step: number;
  blackout: boolean;
};

export type RemoteRevision = {
  revision: number;
  updatedAt: number;
  deckId: string;
  state: RemoteState;
};

/**
 * No 0/O, 1/I/L. A code is read aloud across a hangar and typed on a phone,
 * so the alphabet leaves out the pairs people mishear and mistype.
 */
const CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
const CODE_LENGTH = 6;

/** Long enough that guessing is not worth anyone's afternoon: 31^6 ≈ 887 million. */
export function createRoomCode(random: () => number = Math.random): string {
  let code = '';
  for (let index = 0; index < CODE_LENGTH; index += 1) {
    code += CODE_ALPHABET[Math.floor(random() * CODE_ALPHABET.length)];
  }
  return code;
}

export function isRoomCode(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length === CODE_LENGTH &&
    [...value].every((character) => CODE_ALPHABET.includes(character))
  );
}

/** Human input may be lower-case or spaced when a code is read aloud. */
export function normalizeRoomCode(value: unknown): string {
  return typeof value === 'string' ? value.toUpperCase().replace(/[\s-]+/gu, '') : '';
}

const MAX_PAGE = 5000;
const MAX_STEP = 500;

/** A deck id travels in a URL and addresses a folder, so it stays boring. */
export function isDeckId(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= 64 &&
    /^[a-z0-9][a-z0-9-]*$/u.test(value)
  );
}

/**
 * Absolute position, never a keypress. A relative "forward" that arrives twice
 * or out of order moves the deck somewhere nobody asked for; an absolute state
 * that arrives twice is simply the same slide again.
 */
export function parseRemoteState(value: unknown): RemoteState | null {
  if (!value || typeof value !== 'object') return null;
  const {page, step, blackout} = value as Record<string, unknown>;
  if (!Number.isSafeInteger(page) || (page as number) < 1 || (page as number) > MAX_PAGE) return null;
  if (!Number.isSafeInteger(step) || (step as number) < 0 || (step as number) > MAX_STEP) return null;
  if (typeof blackout !== 'boolean') return null;
  return {page: page as number, step: step as number, blackout};
}

export function sameRemoteState(left: RemoteState, right: RemoteState): boolean {
  return left.page === right.page && left.step === right.step && left.blackout === right.blackout;
}

/**
 * A room outlives a coffee break but not a season. Anything older is treated as
 * an empty room, so a code reused next year cannot resurrect last year's slide.
 */
export const ROOM_TTL_MS = 12 * 60 * 60 * 1000;

export function hasExpired(updatedAt: number, now: number, ttlMs = ROOM_TTL_MS): boolean {
  return now - updatedAt > ttlMs;
}

/**
 * What the server answers, decided in pure code so the rules can be tested
 * without a runtime and cannot drift from what the client believes.
 */
export type RoomAnswer =
  | {status: 200; value: RemoteRevision}
  | {status: 204}
  | {status: 400}
  | {status: 409};

export function readRoom(
  current: RemoteRevision | null,
  since: unknown,
  now: number,
): RoomAnswer {
  if (!current || hasExpired(current.updatedAt, now)) return {status: 204};
  const seen = Number(since);
  // Nothing new is the common answer, and it must stay cheap.
  if (Number.isSafeInteger(seen) && seen >= current.revision) return {status: 204};
  return {status: 200, value: current};
}

export function writeRoom(
  current: RemoteRevision | null,
  body: unknown,
  now: number,
): RoomAnswer {
  const {deckId, state: raw} = (body ?? {}) as Record<string, unknown>;
  const state = parseRemoteState(raw);
  if (!state || !isDeckId(deckId)) return {status: 400};
  const live = current && !hasExpired(current.updatedAt, now) ? current : null;
  // A room belongs to one deck. Two courses sharing a code would otherwise
  // send each other to page numbers that mean nothing in their own deck.
  if (live && live.deckId !== deckId) return {status: 409};
  return {
    status: 200,
    value: {
      // Monotonic even across an expiry, so a client holding an old revision
      // can never be handed something it would consider stale.
      revision: (live?.revision ?? current?.revision ?? 0) + 1,
      updatedAt: now,
      deckId: deckId as string,
      state,
    },
  };
}
